import React, { useEffect, useState } from 'react';
import {
  Heading,
  VStack,
  Text,
  AccordionItem,
  Accordion,
  AccordionButton,
  AccordionIcon,
  Box,
  AccordionPanel,
  Td,
  Table,
  Thead,
  Th,
  TableContainer,
  Tbody,
  Tr,
  Checkbox,
  HStack,
  Button,
  Badge,
  Container,
  Icon,
  useColorModeValue
} from "@chakra-ui/react";
import { FaMoneyBillWave, FaCalendarCheck } from 'react-icons/fa';
import { GoShield } from 'react-icons/go';
import MemberActivityDetails from '../components/member-activity-details';
import { useAuth } from '../components/auth';
import { getMember } from '../services/member-service';
import { Activity } from '../models/activity';
import { getActivitiesFeesByMember } from "../services/product-service";
import { ActivityProductPayment } from "../models/activities-fees-by-member-response";
import { HTTP_STATUS_CODE_OK } from "../constants";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import CurrencyData from "../components/currency-data";
import {MemberProductStatus} from "../models/periodic-product-payment";
import {badgeColor, getBadgeLabel, getDate, paymentCheckboxIsDisabled} from "../helpers";

function MyInsurance(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const [insuranceActivity, setInsuranceActivity] = useState<Activity | undefined>();
  const [activitiesFeesYears, setActivitiesFeesYears] = useState<string[]>([]);
  const [activitiesFeesPayments, setActivitiesFeesPayments] = useState(new Map<string, ActivityProductPayment[]>());
  const [insuranceFeesToPayPayments, setInsuranceFeesToPayPayments] = useState<MemberProductStatus[]>([]);
  const [subtitleText, setSubtitleText] = useState("");

  const cardBg = useColorModeValue('white', 'gray.800');
  const headerBg = useColorModeValue('green.100', 'green.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.300');

  useEffect(() => {
    const getMemberInsurance = async (): Promise<void> => {
      const member = (await getMember(auth?.user?.selectedMember?.id)).response;
      if (member != null) {
        const insurance = member.memberActivities.find((activity) => activity.activity.isInsurance)?.activity;

        if (insurance) {
          setInsuranceActivity(insurance);
          setSubtitleText("Acá podés visualizar y abonar tu seguro.");
        } else {
          setSubtitleText("Actualmente no tienes un seguro contratado.");
        }

        const activitiesFeesResponse = await getActivitiesFeesByMember(member.id);
        if (activitiesFeesResponse.statusCode === HTTP_STATUS_CODE_OK && activitiesFeesResponse.response) {
          const keys = Object.keys(activitiesFeesResponse.response.activityFeesPayments);
          const entries = Object.entries(activitiesFeesResponse.response.activityFeesPayments);
          const map = new Map(entries);
          setActivitiesFeesYears(keys.reverse());
          setActivitiesFeesPayments(map);
        }
      }
    }

    getMemberInsurance().catch(() => {});
  }, [auth?.user?.selectedMember?.id]);

  const handleCheckboxChange = (insuranceActivityId: string, periodicProductCheckbox: MemberProductStatus) => {
    const index = insuranceFeesToPayPayments.findIndex((periodicProductPayment) => periodicProductPayment.product.id === periodicProductCheckbox.product.id);

    if (index >= 0) {
      setInsuranceFeesToPayPayments(insuranceFeesToPayPayments.filter((ppp) => ppp.product.id !== periodicProductCheckbox.product.id));
    } else {
      setInsuranceFeesToPayPayments([...insuranceFeesToPayPayments, periodicProductCheckbox]);
    }
  }

  const getAccordionItemTableRows = (year: string, activityId: string) => {
    const activityFees = activitiesFeesPayments.get(year)?.find((activityFee) => activityFee.activityId === activityId);

    return activityFees?.productPayments.map((activityFee) => {
      return (
        <Tr key={`${activityFee.product.id}-${year}`}>
          <Td>{ isMobile ? activityFee.product.shortName : activityFee.product.name }</Td>
          <Td>
            <Badge colorScheme={badgeColor(activityFee)} variant="solid">
              {getBadgeLabel(activityFee)}
            </Badge>
          </Td>
          <Td>{activityFee.payments?.map(pd => <>{getDate(pd.paymentDate, 'dd-mm-yyyy HH:MM:ss', false)}<br/></>)}</Td>
          <Td><CurrencyData value={activityFee.remainingPrice === 0 ? activityFee.paidAmount : activityFee.remainingPrice}/></Td>
          <Td>{activityFee.paidAmount === 0 ? <></> : <CurrencyData value={activityFee.paidAmount}/>}</Td>
          <Td><Checkbox borderColor="black" disabled={paymentCheckboxIsDisabled(activityFee)} onChange={() => { handleCheckboxChange(activityId, activityFee) }}></Checkbox></Td>
        </Tr>
      )
    })
  }

  const getAccordionItemTable = (year: string, activityId: string) => {
    return (
      <TableContainer>
        <Table variant='simple'>
          <Thead>
            <Tr>
              <Th>Detalle</Th>
              <Th>Estado</Th>
              <Th>Fecha de pago</Th>
              <Th>Valor</Th>
              <Th>Valor abonado</Th>
              <Th>Abonar</Th>
            </Tr>
          </Thead>
          <Tbody>
            {getAccordionItemTableRows(year, activityId)}
          </Tbody>
        </Table>
      </TableContainer>
    )
  }

  const getAccordionItems = (activityId: string) => {
    return activitiesFeesYears.map((year) => {
      return (
        <AccordionItem key={`${activityId}-${year}`}>
          <h2>
            <AccordionButton>
              <Box as="span" flex='1' textAlign='left'>
                <HStack>
                  <Icon as={FaCalendarCheck} color="green.500" />
                  <Text>{year}</Text>
                </HStack>
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4}>
            {getAccordionItemTable(year, activityId)}
          </AccordionPanel>
        </AccordionItem>
      )
    });
  }

  const getInsuranceYearsAccordion = (activityId: string) => {
    return (
      <Accordion defaultIndex={[0]} allowMultiple width="100%">
        {getAccordionItems(activityId)}
      </Accordion>
    )
  }

  const getPaymentTableRows = () => {
    return insuranceFeesToPayPayments.map((activityFeePayment) => {
      return (
        <Tr key={activityFeePayment.product.id}>
          <Td>{activityFeePayment.product.name}</Td>
          <Td><CurrencyData value={activityFeePayment.remainingPrice}/></Td>
        </Tr>
      )
    })
  }

  const handlePayButton = (): void => {
    navigate('/payment-page', {
      state: { productsToPay: insuranceFeesToPayPayments, paymentMethod: 'MercadoPago' }
    });
  };

  const getPaymentSection = () => {
    if (insuranceFeesToPayPayments.length === 0) return <></>

    return (
      <Box
        bg={cardBg}
        p={6}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
        boxShadow="lg"
      >
        <VStack spacing={4} align="stretch">
          <HStack>
            <Icon as={FaMoneyBillWave} color="green.500" />
            <Heading as="h3" size="md" color={textColor}>
              Resumen de pago
            </Heading>
          </HStack>
          <TableContainer>
            <Table variant='simple'>
              <Thead>
                <Tr>
                  <Th>Detalle</Th>
                  <Th>Valor</Th>
                </Tr>
              </Thead>
              <Tbody>
                {getPaymentTableRows()}
              </Tbody>
            </Table>
          </TableContainer>
          <Button
            colorScheme="blue"
            onClick={handlePayButton}
            size="lg"
            width="full"
          >
            Pagar con MercadoPago
          </Button>
        </VStack>
      </Box>
    )
  }

  return (
    <Container maxW="container.xl" py={6}>
      <VStack spacing={6} align="stretch">
        <Box
          bg={headerBg}
          p={6}
          borderRadius="lg"
          border="1px solid"
          borderColor={borderColor}
          boxShadow="sm"
        >
          <HStack>
            <Icon as={GoShield} color="green.500" boxSize={6} />
            <Heading color={textColor}>Mi seguro</Heading>
          </HStack>
          <Text mt={2} color={secondaryTextColor}>
            {subtitleText}
          </Text>
        </Box>

        {/* Content */}
        <VStack spacing={6} align="stretch">
          {/* Insurance Details */}
          {insuranceActivity && (
            <Box
              bg={cardBg}
              borderRadius="lg"
              border="1px solid"
              borderColor={borderColor}
              boxShadow="sm"
              overflow="hidden"
              p={6}
            >
              <VStack spacing={6} align="stretch">
                <MemberActivityDetails activity={insuranceActivity} />
                <Box
                  bg={cardBg}
                  p={6}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={borderColor}
                  boxShadow="lg"
                >
                  <VStack spacing={4} align="stretch">
                    <HStack>
                      <Icon as={FaMoneyBillWave} color="green.500" />
                      <Heading as="h3" size="md" color={textColor}>
                        Historial de pagos
                      </Heading>
                    </HStack>
                    {getInsuranceYearsAccordion(insuranceActivity.id)}
                  </VStack>
                </Box>
              </VStack>
            </Box>
          )}

          {getPaymentSection()}
        </VStack>
      </VStack>
    </Container>
  )
}

export default MyInsurance;
